<template>
  <div>
    <form-wrapper :validator="$v.customer">
      <v-form
        ref="form"
        @keypress.native.enter="submit"
      >
        <v-toolbar
          v-if="formTitle"
          color="grey lighten-3"
        >
          <v-toolbar-title>{{ formTitle }}</v-toolbar-title>
        </v-toolbar>
        <v-card tile>
          <v-container
            fluid
            grid-list-lg
          >
            <v-card-text>
              <v-tabs
                v-model="activeTab"
                background-color="grey lighten-3"
                centered
                class="tabs"
                fixed-tabs
                icons-and-text
                slider-color="transparent"
              >
                <v-tab
                  :key="'main'"
                  href="#tab-main"
                >
                  {{ $t('General') }}
                  <v-icon>fal fa-home</v-icon>
                </v-tab>

                <v-tab
                  :key="'address'"
                  href="#tab-address"
                >
                  {{ $t('Address') }}
                  <v-icon>fal fa-map</v-icon>
                </v-tab>

                <v-tabs-items
                  v-model="activeTab"
                  class="mt-6"
                >
                  <v-tab-item
                    :key="'main'"
                    value="tab-main"
                  >
                    <v-row wrap>
                      <v-col
                        class="pb-0"
                        sm="6"
                      >
                        <form-group name="firstName">
                          <template v-slot:default="{attrs}">
                            <v-text-field
                              ref="firstName"
                              v-bind="attrs"
                              v-model="customer.firstName"
                              :label="$t('First Name')"
                              autofocus
                              filled
                              @blur="$v.customer.firstName.$touch()"
                              @input="$v.customer.firstName.$touch()"
                            />
                          </template>
                        </form-group>
                      </v-col>

                      <v-col
                        sm="6"
                        class="pb-0"
                      >
                        <form-group name="lastName">
                          <template v-slot:default="{attrs}">
                            <v-text-field
                              v-bind="attrs"
                              v-model="customer.lastName"
                              :label="$t('Last Name')"
                              filled
                              @blur="$v.customer.lastName.$touch()"
                              @input="$v.customer.lastName.$touch()"
                            />
                          </template>
                        </form-group>
                      </v-col>

                      <v-col
                        class="pb-0"
                        sm="12"
                      >
                        <v-menu
                          ref="dobmenu"
                          v-model="showDobPicker"
                          :close-on-content-click="false"
                          :nudge-right="40"
                          min-width="280px"
                          offset-y
                          transition="scale-transition"
                        >
                          <template v-slot:activator="{ on }">
                            <v-text-field
                              :label="$t('Date Of Birth')"
                              :value="formattedDateOfBirth"
                              append-icon="fal fa-calendar"
                              filled
                              hide-details
                              readonly
                              v-on="on"
                            />
                          </template>
                          <v-date-picker
                            ref="dobpicker"
                            v-model="customer.dateOfBirth"
                            :max="new Date().toISOString().substr(0, 10)"
                            :scrollable="true"
                            first-day-of-week="1"
                            locale="locale"
                            min="1920-01-01"
                            @change="$refs.dobmenu.save($event)" />
                        </v-menu>
                      </v-col>

                      <v-col
                        class="pb-0"
                        sm="12"
                      >
                        <v-subheader class="pl-0 text-h5 font-weight-bold">{{ $t('Contact Information') }}</v-subheader>
                        <v-divider class="mb-2" />
                      </v-col>

                      <v-col
                        class="pb-0"
                        sm="6"
                      >
                        <form-group name="email">
                          <template v-slot:default="{attrs}">
                            <v-text-field
                              v-bind="attrs"
                              v-model="customer.email"
                              :label="$t('Email Address')"
                              append-icon="fal fa-at"
                              filled
                              @blur="$v.customer.email.$touch()"
                              @input="$v.customer.email.$touch()"
                            />
                          </template>
                        </form-group>
                      </v-col>
                      <v-col
                        class="pb-0"
                        sm="6"
                      >
                        <vue-phone-number-input
                          v-if="cashRegister.store"
                          v-model="phoneMeta.number"
                          :color="cashRegister.store.customThemePrimaryColor"
                          :default-country-code="phoneMeta.country"
                          :preferred-countries="['BE', 'NL', 'FR', 'DE']"
                          :translations="{
                            countrySelectorLabel: $t('Country code'),
                            phoneNumberLabel: $t('Phone'),
                            example: `${$t('Example')}:`
                          }"
                          autocomplete="disabled"
                          size="lg"
                          no-validator-state
                          @update="phoneChanged"
                        />
                      </v-col>

                      <v-col sm="12">
                        <v-select
                          v-model="customer.language"
                          :items="locales"
                          :label="$t('Language')"
                          filled
                          hide-details
                          item-text="value"
                          item-value="key"
                        >
                          <template v-slot:item="{ item }">
                            <flag
                              :locale="item.key"
                              class="mr-5"
                            /> {{ item.value }}
                          </template>
                          <template v-slot:selection="{ item }">
                            <flag
                              :locale="item.key"
                              class="mr-5"
                            /> {{ item.value }}
                          </template>
                        </v-select>
                      </v-col>

                      <v-col
                        class="mt-4"
                        sm="12"
                      >
                        <v-slide-y-transition>
                          <v-switch
                            v-show="customer.email && !$v.customer.email.$error"
                            v-model="customer.allowMarketingEmails"
                            :label="$t('Opt-In to Marketing and Promotional Emails')"
                            class="mt-0"
                            color="primary"
                            hide-details
                            inset
                          />
                        </v-slide-y-transition>
                        <v-slide-y-transition>
                          <v-switch
                            v-show="customer.email && !$v.customer.email.$error"
                            v-model="customer.allowInfoEmails"
                            :label="$t('Opt-In to Informative Emails')"
                            class="mt-1"
                            color="primary"
                            hide-details
                            inset
                          />
                        </v-slide-y-transition>
                      </v-col>
                    </v-row>
                  </v-tab-item>

                  <v-tab-item
                    :key="'address'"
                    value="tab-address"
                  >
                    <v-row wrap>
                      <v-col
                        sm="12"
                        class="pb-0"
                      >
                        <form-group name="test">
                          <template v-slot:default="{attrs}">
                            <vuetify-google-autocomplete
                              id="map"
                              v-bind="attrs"
                              :placeholder="$t('Use The Google API For Address Lookup')"
                              append-icon="fal fa-map"
                              prepend-icon="fal fa-search"
                              @placechanged="getAddressData"
                            />
                          </template>
                        </form-group>
                      </v-col>

                      <v-col
                        class="pb-0"
                        sm="6"
                      >
                        <form-group name="address">
                          <template v-slot:default="{attrs}">
                            <v-text-field
                              v-bind="attrs"
                              v-model="customer.address"
                              :label="$t('Address')"
                              filled
                            />
                          </template>
                        </form-group>
                      </v-col>

                      <v-col
                        class="pb-0"
                        sm="6"
                      >
                        <form-group name="address2">
                          <template v-slot:default="{attrs}">
                            <v-text-field
                              v-bind="attrs"
                              v-model="customer.address2"
                              :label="$t('Apartment, suite, unit, etc.')"
                              filled
                            />
                          </template>
                        </form-group>
                      </v-col>

                      <v-col
                        class="pb-0"
                        sm="6"
                      >
                        <form-group name="postalCode">
                          <template v-slot:default="{attrs}">
                            <v-text-field
                              v-bind="attrs"
                              v-model="customer.postalCode"
                              :label="$t('Postal Code')"
                              autocomplete="off"
                              filled
                            />
                          </template>
                        </form-group>
                      </v-col>

                      <v-col
                        class="pb-0"
                        sm="6"
                      >
                        <form-group name="city">
                          <template v-slot:default="{attrs}">
                            <v-text-field
                              v-bind="attrs"
                              v-model="customer.city"
                              :label="$t('City')"
                              autocomplete="off"
                              filled
                            />
                          </template>
                        </form-group>
                      </v-col>

                      <v-col sm="12">
                        <form-group name="country">
                          <template v-slot:default="{attrs}">
                            <v-autocomplete
                              v-bind="attrs"
                              v-model="customer.country"
                              :items="countries"
                              :label="$t('Select a {0}', [$t('Country').toLowerCase()])"
                              autocomplete="off"
                              filled
                              flat
                              hide-details
                              item-text="name_translated"
                              item-value="iso2"
                            />
                          </template>
                        </form-group>
                      </v-col>

                    </v-row>
                  </v-tab-item>
                </v-tabs-items>

              </v-tabs>

              <v-divider class="mt-4" />
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn
                :loading="$wait.is('saving customer')"
                class="font-weight-bold"
                color="primary"
                text
                @click="submit"
              >{{ $t('Save') }}</v-btn>
            </v-card-actions>
          </v-container>
        </v-card>
      </v-form>
    </form-wrapper>
  </div>
</template>

<script>
import Vue from 'vue'
import { mapState } from 'vuex'
import { required, email } from 'vuelidate/lib/validators'
import moment from 'moment'
import VuePhoneNumberInput from 'vue-phone-number-input'
import 'vue-phone-number-input/dist/vue-phone-number-input.css'
import { parsePhoneNumberFromString } from 'libphonenumber-js'
import isoConv from 'iso-language-converter'
import validationMixin from '@/utils/mixins/validationMixin'
import countriesService from '@/services/countries'
import Flag from '@/components/Flag'

export default {
  name: 'FormsCustomersSave',

  components: {
    VuePhoneNumberInput, Flag
  },

  mixins: [ validationMixin ],

  props: {
    formTitle: {
      default: '',
      type: String
    },
    focusFirstName: {
      default: true,
      type: Boolean
    },
    customerObject: {
      default () {
        return {
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          dateOfBirth: null,
          allowMarketingEmails: false,
          allowInfoEmails: true,
          address: '',
          address2: '',
          postalCode: '',
          city: '',
          country: '',
          language: null
        }
      },
      type: Object
    }
  },

  data () {
    return {
      showDobPicker: false,
      activeTab: 'tab-main',
      customer: null,
      countries: null,
      phoneMeta: null
    }
  },

  computed: {
    ...mapState(['locale', 'cashRegister']),
    formattedDateOfBirth () {
      if (!this.customer.dateOfBirth) return ''
      return moment(this.customer.dateOfBirth).format('D MMMM YYYY')
    },
    locales () {
      const storeLocales = this.$store.getters.locales
      return storeLocales.map((locale) => {
        return { key: locale, value: Vue.i18n.translate(isoConv(locale)) }
      })
    }
  },

  validations () {
    const validations = {
      customer: {
        firstName: { required },
        lastName: { required },
        email: { email }
      }
    }

    return validations
  },

  watch: {
    // show YEAR picker at first when DOB picker is shown
    showDobPicker (val) {
      val && this.$nextTick(() => (this.$refs.dobpicker.activePicker = 'YEAR'))
    },
    // update the customer data with updated customerObject (= prop)
    customerObject: {
      immediate: true,
      handler (customerObject) {
        this.customer = Object.assign({}, customerObject)
        if (!this.customer.language) {
          this.customer.language = this.$store.getters.locale
        }
        this.phoneMeta = { country: 'BE' }
        this.parsePhoneNumber()
      }
    },
    focusFirstName: {
      immediate: true,
      handler (val) {
        if (this.$refs.firstName) {
          this.$nextTick(val ? this.$refs.firstName.focus : this.$refs.firstName.blur)
        }
      }
    }
  },

  created () {
    this.resetActiveTab()
    // fetch countries
    countriesService.list()
      .then((data) => {
        this.countries = data.data.countries
      })
  },

  methods: {
    phoneChanged (data) {
      // set to null if formattedNumber is undefined
      // else wouldn't be saved when number cleared
      this.phoneMeta.formattedNumber = data.formattedNumber || null
    },
    getAddressData (data) {
      if (data) {
        this.customer.address = data.name || ''
        this.customer.postalCode = data.postal_code || ''
        this.customer.city = data.locality || ''
        this.customer.country = this.getISO2(data.country)
      }
    },
    getISO2 (countryName) {
      return _.get(this.countries.find((country) => country.name === countryName), 'iso2')
    },
    resetActiveTab () {
      this.activeTab = 'tab-main'
    },
    parsePhoneNumber () {
      if (this.customer.phone) {
        const phoneNumber = parsePhoneNumberFromString(this.customer.phone)
        if (phoneNumber) {
          this.phoneMeta = {
            'country': phoneNumber.country,
            'number': phoneNumber.formatNational(),
            'formattedNumber': phoneNumber.number
          }
        }
      }
    },
    submit () {
      if (!this.validate()) return
      this.customer.phone = this.phoneMeta.formattedNumber
      this.$wait.start('saving customer')

      return this.$store.dispatch('SAVE_CUSTOMER', this.customer).then((data) => {
        this.$wait.end('saving customer')
        this.helpers.toastSuccess(data.message)
        this.$emit('saved', data.customer)
      }).catch((data) => {
        this.$wait.end('saving customer')
        if (data && data.message) this.helpers.toastError(data.message)
      })
    }
  }
}
</script>

<style language="scss" scoped>
.tabs ::v-deep .v-tab--active,
.tabs ::v-deep .v-tab--active .v-icon {
  background: var(--v-primary-base);
  color: white;
}
</style>

<style language="scss">
  .vue-phone-number-input {
    background-color: rgba(0,0,0,.06);
    border-bottom: 2px solid rgba(0,0,0,.42);
    padding: 0 !important;
  }

  .input-country-selector .field-input,
  .input-phone-number .field-input {
    border: 0 !important;
    height: 55px !important;
    min-height: 55px !important;
    padding-top: 20px !important;
  }

  .input-country-selector .field-input {
    border-top-left-radius: 4px !important;
  }

  .input-phone-number .field-input {
    border-top-right-radius: 4px !important;
  }

  .country-selector .field-country-flag {
    top: 28px !important;
  }

  .country-selector .field-label,
  .input-phone-number .field-label {
    font-size: 12px !important;
    left: 10px !important;
    margin-top: 5px;
  }
</style>
